import type { Item } from "@luckymenu/core";
import { useCallback, useMemo, useState } from "react";
import { canOrder, quickLine } from "../menu/choice";
import { moveHighlight, pickedCounts, pickerChips, pickerItems } from "../menu/picker";
import { type DraftTarget, lineKey } from "../ordering/draft";
import { useMenu } from "./use-menu";
import { useOrderDraft } from "./use-order";

/**
 * The item picker for an order's draft (OP-07): search with short codes
 * first, category chips, the grid, a highlight that the keyboard moves, and
 * the line last added, which + and − change. `pick(item)` adds one at once,
 * or answers `choose` when the item needs a size or add-on chosen first (the
 * app opens its choice dialog or sheet). The menu comes from the device's
 * copy first (`useMenu` reads it back), so the picker opens offline.
 */
export function usePicker(target: DraftTarget) {
	const menu = useMenu();
	const draft = useOrderDraft(target);
	const [query, setQueryState] = useState("");
	const [categoryId, setCategoryState] = useState<string | null>(null);
	const [highlight, setHighlight] = useState(0);
	const [lastLine, setLastLine] = useState<string | null>(null);
	const data = menu.data;
	const groups = data?.modifierGroups ?? [];
	const chips = useMemo(() => (data ? pickerChips(data) : []), [data]);
	const items = useMemo(
		() => (data ? pickerItems(data, { query, categoryId }) : []),
		[data, query, categoryId],
	);
	const counts = useMemo(() => pickedCounts(draft.draft.lines), [draft.draft.lines]);
	// The last line added, while it is still in the draft.
	const selected = draft.draft.lines.find((l) => lineKey(l) === lastLine) ?? null;

	const setQuery = useCallback((value: string) => {
		setQueryState(value);
		setHighlight(0);
	}, []);
	const setCategory = useCallback((id: string | null) => {
		setCategoryState(id);
		setHighlight(0);
	}, []);

	/** Adds one of `item`. `choose`: open the choice for it; `soldOut`: nothing happened. */
	const pick = useCallback(
		(item: Item): "added" | "choose" | "soldOut" => {
			if (!canOrder(item)) return "soldOut";
			const line = quickLine(item, groups);
			if (!line) return "choose";
			draft.add(line);
			setLastLine(lineKey(line));
			return "added";
		},
		[groups, draft.add],
	);

	return {
		status: data ? ("ready" as const) : menu.isError ? ("error" as const) : ("loading" as const),
		retry: () => void menu.refetch(),
		groups,
		chips,
		items,
		/** Units of each item in the draft. */
		counts,
		query,
		setQuery,
		categoryId,
		setCategory,
		/** Index into `items`, or -1 with none. */
		highlight: items.length === 0 ? -1 : Math.min(highlight, items.length - 1),
		setHighlight,
		moveHighlight: useCallback(
			(delta: number) => setHighlight((h) => moveHighlight(h, delta, items.length)),
			[items.length],
		),
		highlighted: items[Math.min(highlight, items.length - 1)] ?? null,
		pick,
		/** For a line made in the choice dialog. */
		addLine: useCallback(
			(line: Parameters<typeof draft.add>[0]) => {
				draft.add(line);
				setLastLine(lineKey(line));
			},
			[draft.add],
		),
		/** The line + and − change: the last one added. */
		selected,
		/** + (1) or − (−1) on the selected line; at 0 it leaves the draft. */
		bump: useCallback(
			(delta: number) => {
				if (lastLine) (delta > 0 ? draft.increment : draft.decrement)(lastLine);
			},
			[lastLine, draft.increment, draft.decrement],
		),
		draft,
	};
}
